import { Box, HStack, useBreakpointValue } from "@chakra-ui/react"
import { CarouselGallery } from "@pathwright/ux/src/components/gallery/Gallery"
import { onMeasureType } from "@pathwright/ux/src/components/text/TruncatedHTML"
import { useEffect, useMemo, useRef, useState } from "react"
import CoverPageDivider from "./CoverPageDivider"
import CoverPageOffer from "./CoverPageOffer"
import { useCoverPageContext } from "./CoverPageProvider"
import { COVER_PAGE_PADDING } from "./constants"
import { type CoverPageOffer as CoverPageOfferType } from "./types"
import { offerHasMeta, offerHasPriceDescription } from "./utils"

const MAX_CARD_HEIGHT = 520

// Local hook to encapsulate dynamic height orchestration
// This allows us to shrink Offer cards down to the height
// of the tallest card
function useDynamicOfferHeights(
  offers: Array<CoverPageOfferType> | undefined,
  galleryRef: React.RefObject<HTMLDivElement>
) {
  const [bodyHeights, setBodyHeights] = useState<
    Record<number, { bodyHeight: number; truncated: boolean }>
  >({})

  const offersHaveMeta = useMemo(() => {
    return offers?.some((offer) => offerHasMeta(offer)) ?? false
  }, [offers])

  const offersHavePriceDescription = useMemo(() => {
    return offers?.some((offer) => offerHasPriceDescription(offer)) ?? false
  }, [offers])

  const allMeasured = useMemo(() => {
    if (!offers) return false
    return offers.every((o) => bodyHeights[o.id]?.bodyHeight != null)
  }, [offers, bodyHeights])

  const anyTruncated = useMemo(() => {
    if (!offers) return false
    return offers.some((o) => bodyHeights[o.id]?.truncated)
  }, [offers, bodyHeights])

  const tallestBody = useMemo(() => {
    if (!offers) return 0
    return offers.reduce(
      (max, o) => Math.max(max, bodyHeights[o.id]?.bodyHeight || 0),
      0
    )
  }, [offers, bodyHeights])

  // Mirror shell measurements from CoverPageOffer to compute full card height
  const titleHeight = 19.55 // 1.15rem * 17px
  const headingSpacer = 8
  const dividerHeight = 21
  const buttonHeight = 47.59
  const priceDescriptionHeight = offersHavePriceDescription ? 12 : 0
  const pricingSpacer = offersHavePriceDescription ? 5 : 0
  const metaHeight = offersHaveMeta ? 12 : 0
  const metaSpacer = offersHaveMeta ? 5 : 0
  const allPadding = 64 // content paddings inside the card

  const titleSectionHeight = titleHeight + headingSpacer
  const bottomSectionHeight =
    dividerHeight +
    buttonHeight +
    priceDescriptionHeight +
    pricingSpacer +
    metaHeight +
    metaSpacer

  const dynamicMaxCardHeight = useMemo(() => {
    if (!allMeasured || anyTruncated) return MAX_CARD_HEIGHT
    const full = Math.round(
      allPadding + titleSectionHeight + bottomSectionHeight + tallestBody
    )
    return Math.min(MAX_CARD_HEIGHT, full)
  }, [
    allMeasured,
    anyTruncated,
    tallestBody,
    titleSectionHeight,
    bottomSectionHeight
  ])

  useEffect(() => {
    if (!galleryRef.current) return
    const ro = new ResizeObserver(() => {
      // Invalidate measurements on width change to re-measure
      setBodyHeights({})
    })
    ro.observe(galleryRef.current)
    return () => ro.disconnect()
  }, [galleryRef])

  const onMeasureBodyFor =
    (id: number): onMeasureType =>
    (info) => {
      const bodyHeight = info.truncated
        ? info.renderedHeight
        : info.naturalHeight
      setBodyHeights((prev) => ({
        ...prev,
        [id]: { bodyHeight, truncated: info.truncated }
      }))
    }

  const forcedBodyHeight =
    !anyTruncated && allMeasured ? tallestBody : undefined

  return {
    offersHaveMeta,
    offersHavePriceDescription,
    dynamicMaxCardHeight,
    tallestBody,
    anyTruncated,
    allMeasured,
    forcedBodyHeight,
    onMeasureBodyFor
  }
}

const CoverPageOffers = () => {
  const { offersSectionRef, offers, immersiveActive } = useCoverPageContext()
  const visibleItems = useBreakpointValue({ base: 1, md: 2, lg: 3 })
  const galleryRef = useRef<HTMLDivElement>(null)

  const {
    offersHaveMeta,
    offersHavePriceDescription,
    dynamicMaxCardHeight,
    forcedBodyHeight,
    onMeasureBodyFor
  } = useDynamicOfferHeights(offers, galleryRef)

  if (!offers?.length) return null

  return (
    <>
      {!immersiveActive && <CoverPageDivider title="Join" titleMargin="1rem" />}
      <Box position="relative" w="100%">
        <HStack
          ref={offersSectionRef}
          w="100%"
          scrollMarginTop="150px"
          role="group"
          sx={{
            ".carousel-item": {
              maxW: "420px",
              overflowY: "visible",
              position: "relative",
              borderRadius: "var(--chakra-radii-card)"
            },
            ".carousel-item-content": {
              borderRadius: "var(--chakra-radii-card)",
              // Allow shadow to be seen
              overflow: "visible"
            }
          }}
        >
          <Box ref={galleryRef} w="100%">
            <CarouselGallery
              items={offers}
              renderItem={(offer) => (
                <CoverPageOffer
                  key={offer.id}
                  offer={offer}
                  displayMeta={offersHaveMeta}
                  displayDescription={offersHavePriceDescription}
                  maxCardHeight={dynamicMaxCardHeight}
                  forceBodyHeight={forcedBodyHeight}
                  onMeasureBody={onMeasureBodyFor(offer.id)}
                />
              )}
              itemsPerRow={visibleItems}
              maxItemHeight={`${dynamicMaxCardHeight}px`}
              gap={0}
              spacingY="0"
              // compensate for padding on the left and right items
              spacingX={`calc(${COVER_PAGE_PADDING.md} - 1rem)`}
              enableStagger
            />
          </Box>
        </HStack>
      </Box>
    </>
  )
}

export default CoverPageOffers
